import { BATTLES_VERSION } from "../content/battles";
import { SHOP_VERSION, itemById, type Item } from "../content/items";
import type { Mode, Op } from "../content/modes";
import { ninjaById, type Ninja } from "../content/ninjas";
import { RANKS } from "../content/ranks";
import type { StripeMarks } from "../content/trials";
import {
  HISTORY_LIMIT,
  MISSED_LIMIT,
  type HistoryEntry,
  type Settings,
  type TierStat,
  type TrialRecord,
} from "../save/schema";
import type { BattleResult } from "./battle";
import { bestCombo } from "./combo";
import { battleCoins, practiceCoins, trialCoins } from "./coins";
import { applyPurchase as buyLevel, balanceOf, mergeItems, purchaseAllowed } from "./items";
import {
  newId,
  type BattleEvent,
  type BaselineEvent,
  type PlayEvent,
  type PracticeEvent,
  type Progress,
  type PurchaseEvent,
  type RankEvent,
  type TrialEvent,
} from "./events";
import { recordAnswers } from "./mastery";
import { enabledOps } from "./modes";
import { questionType } from "./questions/types";
import { isRecruited, mergeRosters, recordBattle } from "./roster";
import type { Answer } from "./round";
import { updateSets, type SetMap } from "./sets";
import { addStripes, nextStripeMark, stripesFor } from "./stripes";
import { nextTrial, trialFor, type TrialSpec } from "./trials";

export interface RoundResult {
  score: number;
  tried: number;
  log: Answer[];
  /** How long the round actually lasted. */
  durationMs: number | null;
}

export interface TrialOutcome {
  kind: "trial";
  mode: Mode;
  /** The rank the trial was for. */
  rankIndex: number;
  passMark: number;
  score: number;
  tried: number;
  passed: boolean;
  wrong: number;
  /** Questions passed with the Pass button (counted in `wrong`). */
  passes: number;
  /** Stripe marks on this trial, and stripes held on it before and after. */
  stripeMarks: StripeMarks;
  stripesBefore: number;
  stripes: number;
  /** Cards that turned gold or foil, and the overall rank before and after. */
  newGold: number[];
  newFoil: number[];
  overallBefore: number;
  overall: number;
  /** Best on this trial before and after. */
  prevBest: number;
  best: number;
  /** Rank held after the trial. */
  rank: number;
  rankedUp: boolean;
  /** Coins this trial paid (a first pass). */
  coins: number;
  /** The longest run of right answers. */
  bestCombo: number;
  banner: string;
  detail: string;
  nextText: string;
}

export interface PracticeOutcome {
  kind: "practice";
  mode: Mode;
  score: number;
  tried: number;
  wrong: number;
  passes: number;
  coins: number;
  /** The longest run of right answers. */
  bestCombo: number;
}

export interface BattleOutcome {
  kind: "battle";
  mode: Mode;
  enemy: Ninja;
  fighter: Ninja;
  end: BattleEvent["end"];
  won: boolean;
  /** Joined the roster with this win. */
  recruited: boolean;
  score: number;
  tried: number;
  wrong: number;
  passes: number;
  points: number;
  damage: number;
  hits: number;
  coins: number;
  /** Of which, a worn Lucky Charm's share. */
  coinBonus: number;
  /** Coins the child can spend after the battle. */
  balance: number;
  /** The longest run of right answers. */
  bestCombo: number;
  /** The active items pressed. */
  itemsUsed: Item[];
  banner: string;
  detail: string;
}

export type Outcome = TrialOutcome | PracticeOutcome | BattleOutcome;

/** The date on this device (not UTC), YYYY-MM-DD. */
export function isoDate(date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}

function stamp(now: Date) {
  return { id: newId(), at: now.toISOString(), day: isoDate(now) };
}

// ─── Wording ────────────────────────────────────────────────────────────────

export function trialBanner(
  passed: boolean,
  score: number,
  prevBest: number,
  newStripes = 0,
): string {
  if (passed) return "Trial passed!";
  if (newStripes > 1) return `${newStripes} stripes earned!`;
  if (newStripes === 1) return "Stripe earned!";
  if (score > prevBest && prevBest > 0) return "New best on this trial!";
  return "Not this time";
}

export function trialDetail(score: number, passMark: number, passed: boolean): string {
  if (passed) return `${score} correct. You needed ${passMark}.`;
  const gap = passMark - score;
  return `${score} of ${passMark} needed. ${gap <= 3 ? "So close!" : "Keep training!"}`;
}

/** What to aim for next, after a trial. */
export function nextTrialText(
  mode: Mode,
  rank: number,
  passed: boolean,
  best: number,
  marks?: StripeMarks,
): string {
  if (!passed) {
    const mark = marks ? nextStripeMark(best, marks) : null;
    return `Your best on this trial: ${best}.` + (mark ? ` Next stripe at ${mark}.` : "");
  }
  const nx = nextTrial(mode, rank);
  if (!nx) return "You've reached the top rank!";
  return `Next: the ${RANKS[nx.rankIndex]!.name} trial. Pass mark ${nx.passMark}.`;
}

export function battleBanner(end: BattleEvent["end"], recruited: boolean): string {
  if (end === "won") return recruited ? "Recruited!" : "Victory!";
  if (end === "knockout") return "Knocked out!";
  if (end === "retreat") return "You retreated";
  return "Time's up!";
}

/** "Storm Ninja joins your roster." and the like. */
export function battleDetail(
  end: BattleEvent["end"],
  enemy: Ninja,
  recruited: boolean,
  damage: number,
): string {
  const left = Math.max(0, enemy.health - damage);
  if (end === "won")
    return recruited ? `${enemy.name} joins your roster!` : `${enemy.name} is beaten again.`;
  if (end === "knockout") return `Time for a rest. ${enemy.name} had ${left} health left.`;
  if (end === "retreat")
    return `${enemy.name} lives to fight another day, with ${left} health left.`;
  return `${enemy.name} slipped away with ${left} health left.`;
}

// ─── Making events ──────────────────────────────────────────────────────────

/** Whether passing this trial now would be the child's first pass of it. */
export function firstPassOf(p: Progress, spec: TrialSpec): boolean {
  return !p.trials[spec.mode]?.[String(spec.rankIndex)]?.passed;
}

export function trialEvent(
  spec: TrialSpec,
  round: RoundResult,
  seed: number,
  settings: Pick<Settings, "audio" | "ops">,
  now = new Date(),
  /** A first pass pays a coin bonus; decided on the device (see coins.ts). */
  firstPass = true,
): TrialEvent {
  const passed = round.score >= spec.passMark;
  return {
    ...stamp(now),
    kind: "trial",
    mode: spec.mode,
    rankIndex: spec.rankIndex,
    score: round.score,
    tried: round.tried,
    seconds: spec.seconds,
    passed,
    audio: settings.audio,
    seed,
    trialsVersion: spec.version,
    ops: enabledOps(settings.ops),
    coins: trialCoins(spec.rankIndex, passed && firstPass),
    durationMs: round.durationMs,
    answers: round.log,
  };
}

export function practiceEvent(
  mode: Mode,
  round: RoundResult,
  settings: Pick<Settings, "audio" | "practice" | "ops">,
  now = new Date(),
): PracticeEvent {
  return {
    ...stamp(now),
    kind: "practice",
    mode,
    score: round.score,
    tried: round.tried,
    seconds: settings.practice.minutes * 60,
    audio: settings.audio,
    ops: enabledOps(settings.ops),
    coins: practiceCoins(round.score),
    durationMs: round.durationMs,
    answers: round.log,
  };
}

export function battleEvent(
  b: { mode: Mode; enemy: Ninja; fighter: Ninja; seconds: number; seed: number },
  result: BattleResult & { durationMs: number | null },
  settings: Pick<Settings, "audio" | "ops">,
  now = new Date(),
): BattleEvent {
  const won = result.end === "won";
  return {
    ...stamp(now),
    kind: "battle",
    mode: b.mode,
    enemy: b.enemy.id,
    fighter: b.fighter.id,
    seconds: b.seconds,
    score: result.score,
    tried: result.tried,
    points: result.points,
    damage: result.damage,
    hits: result.hits,
    won,
    end: result.end,
    moveUsed: result.moveUsed,
    items: result.items,
    itemsUsed: result.itemsUsed,
    coins: battleCoins(result.points, won, b.enemy.health, result.coinShare),
    seed: b.seed,
    audio: settings.audio,
    battlesVersion: BATTLES_VERSION,
    ops: enabledOps(settings.ops),
    durationMs: result.durationMs,
    answers: result.log,
  };
}

export function purchaseEvent(
  item: string,
  level: number,
  price: number,
  now = new Date(),
): PurchaseEvent {
  return { ...stamp(now), kind: "purchase", item, level, price, shopVersion: SHOP_VERSION };
}

export function rankEvent(
  mode: Mode,
  rankIndex: number,
  reason: RankEvent["reason"],
  ops: Record<Op, boolean> | null = null,
  now = new Date(),
): RankEvent {
  return {
    ...stamp(now),
    kind: "rank",
    mode,
    rankIndex,
    reason,
    ...(ops ? { ops: enabledOps(ops) } : {}),
  };
}

export function baselineEvent(progress: Progress, now = new Date()): BaselineEvent {
  return { ...stamp(now), kind: "baseline", progress };
}

// ─── Applying events ────────────────────────────────────────────────────────

const clampRank = (r: number) => Math.max(0, Math.min(r, RANKS.length - 1));

function withAnswers(p: Progress, log: readonly Answer[], day: string) {
  return recordAnswers(p.mastery, p.missed, log, day, MISSED_LIMIT);
}

/** Answers, right answers and time taken per tier, for tuning the trials. Pure. */
/** How many questions the child passed on rather than answered. */
export function passCount(log: readonly Answer[]): number {
  return log.filter((a) => a.passed).length;
}

export function tierSummary(log: readonly Answer[]): Record<string, TierStat> {
  const out: Record<string, TierStat> = {};
  for (const a of log) {
    const tier = questionType(a.typeId)?.tier ?? 0;
    const t = (out[String(tier)] ??= { n: 0, right: 0, ms: 0 });
    t.n += 1;
    if (a.correct) t.right += 1;
    t.ms += Math.round(a.ms ?? 0);
  }
  return out;
}

const opsRecord = (ops: readonly Op[]): Record<Op, boolean> => ({
  add: ops.includes("add"),
  sub: ops.includes("sub"),
  mul: ops.includes("mul"),
  div: ops.includes("div"),
});

/**
 * Bring gold and foil cards and the overall rank up to date with the ranks
 * held, for the strands switched on (`ops`). Only ever adds. Pure.
 */
export function withSets(p: Progress, ops: readonly Op[] | Record<Op, boolean>, day: string) {
  const on = Array.isArray(ops) ? opsRecord(ops) : (ops as Record<Op, boolean>);
  const u = updateSets({ rank: p.rank, ops: on, sets: p.sets, overall: p.overall }, day);
  return {
    progress: { ...p, sets: u.sets, overall: u.overall },
    newGold: u.newGold,
    newFoil: u.newFoil,
  };
}

/** Newest first; ties keep their order, so the latest event wins. */
function addHistory(history: HistoryEntry[], entry: HistoryEntry): HistoryEntry[] {
  const i = history.findIndex((h) => h.d <= entry.d);
  const next = i < 0 ? [...history, entry] : [...history.slice(0, i), entry, ...history.slice(i)];
  return next.slice(0, HISTORY_LIMIT);
}

/**
 * A finished trial: attempts, best, pass date, rank, history and mastery.
 * Ranks are never lost: failing changes only attempts and best. Pure.
 */
export function applyTrial(
  p: Progress,
  e: TrialEvent,
): { progress: Progress; outcome: TrialOutcome } {
  const { mode, rankIndex } = e;
  const key = String(rankIndex);
  const rec = p.trials[mode]?.[key] ?? { best: 0, attempts: 0 };
  const best = Math.max(rec.best, e.score);
  const oldRank = p.rank[mode];
  const rank = e.passed ? Math.max(oldRank, clampRank(rankIndex)) : oldRank;
  const passedOn = rec.passed ?? (e.passed ? e.day : undefined);
  const spec = trialFor(mode, rankIndex);
  const passMark = spec?.passMark ?? 0;
  const wrong = e.tried - e.score;
  const passes = passCount(e.answers);

  // Stripes use today's marks, so older results earn them as the migration did.
  const stripesBefore = p.stripes[mode]?.[key] ?? 0;
  const stripes = spec
    ? addStripes(p.stripes, mode, rankIndex, stripesFor(e.score, spec.stripes))
    : p.stripes;
  const stripesAfter = stripes[mode]?.[key] ?? 0;

  const entry: HistoryEntry = {
    d: e.day,
    mode,
    kind: "trial",
    rankIndex,
    score: e.score,
    tried: e.tried,
    seconds: e.seconds,
    passed: e.passed,
    audio: e.audio,
    seed: e.seed,
    ...(mode === "mix" && e.ops ? { ops: e.ops } : {}),
    trialsVersion: e.trialsVersion,
    wrong,
    ...(passes ? { passes } : {}),
    tiers: tierSummary(e.answers),
    ...(e.coins ? { coins: e.coins } : {}),
  };

  const ranked: Progress = {
    ...p,
    rank: { ...p.rank, [mode]: rank },
    coins: p.coins + (e.coins ?? 0),
    stripes,
    trials: {
      ...p.trials,
      [mode]: {
        ...p.trials[mode],
        [key]: {
          best,
          attempts: rec.attempts + 1,
          lastSeed: e.seed,
          ...(passedOn ? { passed: passedOn } : {}),
        },
      },
    },
    history: addHistory(p.history, entry),
    ...withAnswers(p, e.answers, e.day),
  };

  // Gold, foil and the overall rank: whatever was already complete before
  // this trial doesn't count as new.
  const before = e.ops ? withSets(p, e.ops, e.day).progress : p;
  const after = e.ops
    ? withSets({ ...ranked, sets: before.sets, overall: before.overall }, e.ops, e.day)
    : { progress: ranked, newGold: [], newFoil: [] };

  return {
    progress: after.progress,
    outcome: {
      kind: "trial",
      mode,
      rankIndex,
      passMark,
      score: e.score,
      tried: e.tried,
      passed: e.passed,
      wrong,
      passes,
      stripeMarks: spec?.stripes ?? [0, 0, 0],
      stripesBefore,
      stripes: stripesAfter,
      newGold: after.newGold,
      newFoil: after.newFoil,
      overallBefore: before.overall,
      overall: after.progress.overall,
      prevBest: rec.best,
      best,
      rank,
      rankedUp: rank > oldRank,
      coins: e.coins ?? 0,
      bestCombo: bestCombo(e.answers),
      banner: trialBanner(e.passed, e.score, rec.best, stripesAfter - stripesBefore),
      detail: trialDetail(e.score, passMark, e.passed),
      nextText: nextTrialText(mode, rank, e.passed, best, spec?.stripes),
    },
  };
}

/** A finished practice: history and mastery only. No rank, no best. Pure. */
export function applyPractice(
  p: Progress,
  e: PracticeEvent,
): { progress: Progress; outcome: PracticeOutcome } {
  const wrong = e.tried - e.score;
  const passes = passCount(e.answers);
  const entry: HistoryEntry = {
    d: e.day,
    mode: e.mode,
    kind: "practice",
    score: e.score,
    tried: e.tried,
    seconds: e.seconds,
    audio: e.audio,
    wrong,
    ...(passes ? { passes } : {}),
    tiers: tierSummary(e.answers),
    ...(e.coins ? { coins: e.coins } : {}),
  };
  return {
    progress: {
      ...p,
      history: addHistory(p.history, entry),
      coins: p.coins + (e.coins ?? 0),
      ...withAnswers(p, e.answers, e.day),
    },
    outcome: {
      kind: "practice",
      mode: e.mode,
      score: e.score,
      tried: e.tried,
      wrong,
      passes,
      coins: e.coins ?? 0,
      bestCombo: bestCombo(e.answers),
    },
  };
}

/**
 * A finished battle: the roster (a first win recruits), coins, history and
 * mastery. Recruiting happens here, in the fold, so every device agrees
 * however many of them beat the same ninja. Pure.
 */
export function applyBattle(
  p: Progress,
  e: BattleEvent,
): { progress: Progress; outcome: BattleOutcome } {
  const enemy = ninjaById(e.enemy) ?? ninjaById("seedling")!;
  const fighter = ninjaById(e.fighter) ?? ninjaById("seedling")!;
  const recruited = e.won && !isRecruited(p.roster, e.enemy);
  const wrong = e.tried - e.score;
  const passes = passCount(e.answers);
  const entry: HistoryEntry = {
    d: e.day,
    mode: e.mode,
    kind: "battle",
    enemy: e.enemy,
    fighter: e.fighter,
    won: e.won,
    coins: e.coins,
    score: e.score,
    tried: e.tried,
    seconds: e.seconds,
    audio: e.audio,
    seed: e.seed,
    ...(e.mode === "mix" && e.ops ? { ops: e.ops } : {}),
    ...(e.items?.length ? { items: e.items } : {}),
    ...(e.itemsUsed?.length ? { itemsUsed: e.itemsUsed } : {}),
    wrong,
    ...(passes ? { passes } : {}),
    tiers: tierSummary(e.answers),
  };
  const coins = p.coins + e.coins;
  return {
    progress: {
      ...p,
      roster: recordBattle(p.roster, e.enemy, e.won, e.day),
      coins,
      history: addHistory(p.history, entry),
      ...withAnswers(p, e.answers, e.day),
    },
    outcome: {
      kind: "battle",
      mode: e.mode,
      enemy,
      fighter,
      end: e.end,
      won: e.won,
      recruited,
      score: e.score,
      tried: e.tried,
      wrong,
      passes,
      points: e.points,
      damage: e.damage,
      hits: e.hits,
      coins: e.coins,
      coinBonus: Math.max(0, e.coins - battleCoins(e.points, e.won, enemy.health)),
      balance: balanceOf({ coins, spent: p.spent }),
      bestCombo: bestCombo(e.answers),
      itemsUsed: (e.itemsUsed ?? []).flatMap((id) => itemById(id) ?? []),
      banner: battleBanner(e.end, recruited),
      detail: battleDetail(e.end, enemy, recruited, e.damage),
    },
  };
}

/**
 * A purchase: the item's level rises and the price is spent, if the level is
 * the next one and the balance covers it; otherwise nothing changes (a
 * double-buy from another device, or coins spent elsewhere first). Pure.
 */
export function applyPurchase(p: Progress, e: PurchaseEvent): Progress {
  if (!purchaseAllowed(p.items, balanceOf(p), e.item, e.level, e.price)) return p;
  return { ...p, items: buyLevel(p.items, e.item, e.level, e.day), spent: p.spent + e.price };
}

/**
 * Whether the one-off v1.1 award is due in `mode`: not had yet, and the best
 * on the next trial already meets that trial's (new, lower) pass mark.
 */
export function retuneDue(p: Progress, mode: Mode): boolean {
  if (p.retuned.includes(mode)) return false;
  const next = nextTrial(mode, p.rank[mode]);
  const best = next ? (p.trials[mode]?.[String(next.rankIndex)]?.best ?? 0) : 0;
  return !!next && best >= next.passMark;
}

/**
 * Rank changes. Placement never lowers a rank; a parent's change sets it
 * exactly. A retune award raises the rank by one if it's still due when
 * folded, and happens at most once per strand however many devices send it.
 * Pure.
 */
export function applyRank(p: Progress, e: RankEvent): Progress {
  let next: Progress;
  if (e.reason === "retune") {
    if (p.retuned.includes(e.mode)) return p;
    const retuned = [...p.retuned, e.mode];
    if (!retuneDue(p, e.mode)) return { ...p, retuned };
    const r = p.rank[e.mode] + 1;
    const key = String(r);
    const rec = p.trials[e.mode]?.[key] ?? { best: 0, attempts: 0 };
    next = {
      ...p,
      retuned,
      rank: { ...p.rank, [e.mode]: r },
      trials: {
        ...p.trials,
        [e.mode]: { ...p.trials[e.mode], [key]: { ...rec, passed: rec.passed ?? e.day } },
      },
    };
  } else {
    const r = clampRank(e.rankIndex);
    const rank = e.reason === "placement" ? Math.max(p.rank[e.mode], r) : r;
    next = { ...p, rank: { ...p.rank, [e.mode]: rank } };
  }
  return e.ops ? withSets(next, e.ops, e.day).progress : next;
}

function mergeTrial(a: TrialRecord | undefined, b: TrialRecord): TrialRecord {
  if (!a) return b;
  const passed = [a.passed, b.passed].filter((d): d is string => !!d).sort()[0];
  const lastSeed = b.lastSeed ?? a.lastSeed;
  return {
    best: Math.max(a.best, b.best),
    attempts: a.attempts + b.attempts,
    ...(passed ? { passed } : {}),
    ...(lastSeed !== undefined ? { lastSeed } : {}),
  };
}

const earliest = (a: string | undefined, b: string | undefined) =>
  a && b ? (a < b ? a : b) : (a ?? b);

/**
 * Merge imported progress into a child's: the higher rank, combined trial
 * records, history and mastery, and every stripe and set either had. Used
 * when a device's old local save joins a profile. Pure.
 */
export function mergeProgress(p: Progress, b: Progress): Progress {
  const rank = { ...p.rank };
  for (const m of Object.keys(rank) as Mode[]) rank[m] = Math.max(rank[m], clampRank(b.rank[m]));

  const trials = { ...p.trials };
  for (const [m, recs] of Object.entries(b.trials) as [Mode, Record<string, TrialRecord>][]) {
    const merged = { ...trials[m] };
    for (const [k, r] of Object.entries(recs)) merged[k] = mergeTrial(merged[k], r);
    trials[m] = merged;
  }

  let history = p.history;
  for (const h of b.history) history = addHistory(history, h);

  const mastery = { ...p.mastery };
  for (const [k, m] of Object.entries(b.mastery)) {
    const a = mastery[k];
    mastery[k] = a
      ? {
          seen: a.seen + m.seen,
          correct: a.correct + m.correct,
          streak: m.last >= a.last ? m.streak : a.streak,
          last: m.last >= a.last ? m.last : a.last,
        }
      : m;
  }

  const missed = { ...p.missed };
  for (const [k, m] of Object.entries(b.missed))
    missed[k] = { n: (missed[k]?.n ?? 0) + m.n, type: m.type };

  let stripes = p.stripes;
  for (const [m, recs] of Object.entries(b.stripes) as [Mode, Record<string, number>][])
    for (const [k, n] of Object.entries(recs)) stripes = addStripes(stripes, m, Number(k), n);

  const sets: SetMap = { ...p.sets };
  for (const [k, s] of Object.entries(b.sets)) {
    const gold = earliest(sets[k]?.gold, s.gold);
    const foil = earliest(sets[k]?.foil, s.foil);
    sets[k] = { ...(gold ? { gold } : {}), ...(foil ? { foil } : {}) };
  }

  return {
    rank,
    trials,
    history,
    mastery,
    missed,
    stripes,
    sets,
    overall: Math.max(p.overall, b.overall),
    retuned: [...new Set([...p.retuned, ...b.retuned])],
    coins: p.coins + b.coins,
    roster: mergeRosters(p.roster, b.roster),
    spent: p.spent + b.spent,
    items: mergeItems(p.items, b.items),
  };
}

/** Apply any event. Pure. */
export function applyEvent(p: Progress, e: PlayEvent): Progress {
  switch (e.kind) {
    case "trial":
      return applyTrial(p, e).progress;
    case "practice":
      return applyPractice(p, e).progress;
    case "battle":
      return applyBattle(p, e).progress;
    case "purchase":
      return applyPurchase(p, e);
    case "rank":
      return applyRank(p, e);
    case "baseline":
      return mergeProgress(p, e.progress);
  }
}

export function foldEvents(p: Progress, events: readonly PlayEvent[]): Progress {
  return events.reduce(applyEvent, p);
}
